import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { 
  MapPin, Clock, Banknote, Calendar, ChevronRight, ChevronDown, PlayCircle,
  CheckCircle2, Send, Loader2, MessageSquare, Ban, AlertTriangle, XCircle, CalendarRange
} from 'lucide-react';
import { toast } from 'sonner';
import { getCoverageDisplay } from '../../utils/coverageDisplay';

export default function MyShiftsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [shifts, setShifts] = useState([]);
  const [appliedShifts, setAppliedShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phase 5 UX (Feb 2026): 3-tab structure — Waiting / Confirmed / Past.
  // The "Applied" and "Upcoming/Backup" states are all pre-decision and
  // now live together under "Waiting". Active (in_progress) sits under
  // "Confirmed" alongside upcoming assigned shifts. Completed +
  // cancelled/no-show all go to "Past". Query string `?tab=waiting|
  // confirmed|past` can also drive it (used by the Find Shifts success
  // dialog when it links here).
  const initialTab = useMemo(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return ['waiting', 'confirmed', 'past'].includes(t) ? t : 'confirmed';
  }, [location.search]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [expandedApps, setExpandedApps] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [myRes, appliedRes] = await Promise.all([
        api.get('/shifts/my-shifts'),
        api.get('/shifts/worker/applied')
      ]);
      setShifts(myRes.data);
      setAppliedShifts(appliedRes.data);
      // Also trigger till reminder check and shift reminder check
      try { api.post('/till-reminders/check'); } catch {}
      try { api.post('/shift-reminders/check'); } catch {}
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Phase 5 UX: 3-bucket categorization.
  //   waiting   → applications + assigned shifts that haven't started
  //              (the worker is waiting for the shift day)
  //   confirmed → assigned + in_progress that are the worker's live/next
  //              shift (today's shift is surfaced separately at the top)
  //   past      → completed + cancelled + no-show
  //
  // Because "Waiting" merges both "pending application" and "assigned but
  // not yet started" the AppliedShiftCard still handles applications-only.
  const categorizeShifts = () => {
    const confirmed = [];
    const past = [];
    shifts.forEach(shift => {
      if (shift.status === 'in_progress' || shift.status === 'assigned') confirmed.push(shift);
      else if (shift.status === 'completed' || shift.status === 'cancelled' || shift.status === 'no_show') past.push(shift);
    });
    return { confirmed, past };
  };

  const { confirmed, past } = categorizeShifts();

  // Today's live shift = the in_progress one (or the first assigned one
  // scheduled for today). Surfaced at the top for one-tap access.
  const todaysShift = useMemo(() => {
    const live = confirmed.find(s => s.status === 'in_progress');
    if (live) return live;
    const todayStr = new Date().toISOString().slice(0, 10);
    return confirmed.find(s => s.status === 'assigned' && s.date === todayStr) || null;
  }, [confirmed]);

  const toggleExpanded = (id) => {
    setExpandedApps(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const isWithin24Hours = (shift) => {
    try {
      const shiftStart = new Date(`${shift.date}T${shift.scheduledStartTime}:00Z`);
      const now = new Date();
      return (shiftStart - now) / (1000 * 60 * 60) < 24;
    } catch { return false; }
  };

  const handleCancelShift = async (shiftId) => {
    setCancelling(shiftId);
    setConfirmCancel(null);
    try {
      const res = await api.post(`/shifts/${shiftId}/worker-cancel`);
      if (res.data.penalty > 0) {
        toast.error(`Shift cancelled. A £${res.data.penalty} penalty was applied.`);
      } else {
        toast.success('Shift cancelled successfully.');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel shift');
    } finally {
      setCancelling(null);
    }
  };

  const handleWithdrawApplication = async (applicationId) => {
    setWithdrawing(applicationId);
    try {
      await api.post(`/applications/${applicationId}/withdraw`);
      toast.success('Application withdrawn');
      setAppliedShifts(prev => prev.filter(s => s.applicationId !== applicationId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw application');
    } finally {
      setWithdrawing(null);
    }
  };

  const getStatusBadge = (status) => {
    // Phase 5 UX: worker-facing wording is friendlier and self-explanatory.
    const styles = {
      assigned: 'bg-emerald-100 text-emerald-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-slate-100 text-slate-700',
      pending: 'bg-amber-100 text-amber-800',
      backup: 'bg-amber-100 text-amber-800',
      cancelled: 'bg-gray-100 text-gray-600',
      no_show: 'bg-red-100 text-red-800',
    };
    const labels = {
      assigned: 'Confirmed', in_progress: 'In progress', completed: 'Completed',
      pending: 'Waiting', backup: 'Backup', cancelled: 'Cancelled', no_show: 'No show',
    };
    return <Badge className={`${styles[status] || 'bg-slate-100'} font-medium`}>{labels[status] || status}</Badge>;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  /* Collapsible Application Card */
  const AppliedShiftCard = ({ shift }) => {
    const isOpen = expandedApps[shift.id] || false;
    return (
      <Collapsible open={isOpen} onOpenChange={() => toggleExpanded(shift.id)}>
        <Card className="border-0 shadow-sm" data-testid={`applied-shift-${shift.id}`}>
          <CollapsibleTrigger asChild>
            <CardContent className="p-4 cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Send className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{shift.branchName}</h3>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                      <span>{formatDate(shift.date)}</span>
                      <span>{shift.scheduledStartTime} - {shift.scheduledEndTime}</span>
                      <span className="font-medium">&pound;{shift.pay?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {getStatusBadge(shift.applicationStatus || 'pending')}
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </CardContent>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              <div className="flex items-center gap-1 text-slate-500 text-sm">
                <MapPin className="h-4 w-4" />
                <span className="truncate">{shift.location}</span>
              </div>
              {(() => {
                const cov = getCoverageDisplay(shift);
                if (!cov) return null;
                return (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg" data-testid={`applied-coverage-badge-${shift.id}`}>
                    <CalendarRange className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                    <span className="text-xs font-medium text-blue-800">{cov.full}</span>
                  </div>
                );
              })()}
              {shift.applicationStatus === 'backup' ? (
                <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg" data-testid={`backup-status-msg-${shift.id}`}>
                  <AlertTriangle className="h-4 w-4" />
                  <span>You're on the backup list — you'll be notified if the assigned worker cancels</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
                  <Send className="h-4 w-4" />
                  <span>Application pending — awaiting manager review</span>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline" className="flex-1 h-10 rounded-full text-sm"
                  onClick={(e) => { e.stopPropagation(); navigate(`/worker/chat?shiftId=${shift.id}&workerId=self`); }}
                  data-testid={`message-manager-btn-${shift.id}`}
                >
                  <MessageSquare className="h-4 w-4 mr-2" /> Message Manager
                </Button>
                <Button
                  variant="outline"
                  className="h-10 rounded-full text-sm text-red-600 border-red-200 hover:bg-red-50"
                  onClick={(e) => { e.stopPropagation(); handleWithdrawApplication(shift.applicationId); }}
                  disabled={withdrawing === shift.applicationId}
                  data-testid={`withdraw-application-btn-${shift.id}`}
                >
                  {withdrawing === shift.applicationId ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    );
  };

  /* Standard Shift Card */
  const ShiftCard = ({ shift }) => {
    const penaltyWarning = shift.status === 'assigned' && isWithin24Hours(shift);
    return (
      <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`my-shift-${shift.id}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-slate-900">{shift.branchName}</h3>
              <div className="flex items-center gap-1 text-slate-500 mt-1">
                <MapPin className="h-4 w-4" />
                <span className="text-sm truncate">{shift.location}</span>
              </div>
            </div>
            {getStatusBadge(shift.status)}
          </div>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-4">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4 text-slate-400" />
              <span>{formatDate(shift.date)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-slate-400" />
              <span>{shift.scheduledStartTime} - {shift.scheduledEndTime}</span>
            </div>
            <div className="flex items-center gap-1">
              <Banknote className="h-4 w-4 text-slate-400" />
              <span>&pound;{shift.pay?.toFixed(2)}</span>
            </div>
          </div>

          {(() => {
            const cov = getCoverageDisplay(shift);
            if (!cov) return null;
            return (
              <div className="flex items-center gap-2 mb-4 px-3 py-1.5 bg-blue-50 rounded-lg" data-testid={`coverage-badge-${shift.id}`}>
                <CalendarRange className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <span className="text-xs font-medium text-blue-800">{cov.full}</span>
              </div>
            );
          })()}

          {shift.status === 'assigned' && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button 
                  className="flex-1 h-12 rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white font-medium"
                  onClick={() => navigate(`/worker/tracker?shiftId=${shift.id}`)}
                  data-testid={`open-shift-btn-${shift.id}`}
                >
                  Open Today&apos;s Shift <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
                <Button 
                  variant="outline"
                  className="h-12 rounded-full text-orange-600 border-orange-200 hover:bg-orange-50"
                  onClick={() => setConfirmCancel(shift)}
                  disabled={cancelling === shift.id}
                  data-testid={`cancel-shift-btn-${shift.id}`}
                >
                  {cancelling === shift.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                </Button>
              </div>
              {penaltyWarning && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>Cancelling within 24h incurs a &pound;20 penalty</span>
                </div>
              )}
            </div>
          )}

          {shift.status === 'in_progress' && (
            <div className="space-y-2">
              <Button 
                className="w-full h-12 rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white font-medium"
                onClick={() => navigate(`/worker/tracker?shiftId=${shift.id}`)}
                data-testid={`open-shift-btn-${shift.id}`}
              >
                <PlayCircle className="h-4 w-4 mr-2" /> Open Today&apos;s Shift <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              {/* Phase 1 UX correction: contextual Till Check entry.
                  Routes to the existing till-checks page pre-scoped to
                  this shift — the old top-level "Till" tab is gone. */}
              <Button 
                variant="outline"
                className="w-full h-12 rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 font-medium"
                onClick={() => navigate(`/worker/till-checks?shiftId=${shift.id}`)}
                data-testid={`complete-till-check-btn-${shift.id}`}
              >
                <Banknote className="h-4 w-4 mr-2" /> Complete Till Check
              </Button>
            </div>
          )}

          {shift.status === 'completed' && shift.timeLog && (
            <div className="pt-3 border-t border-slate-100 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
                <span>{shift.timeLog.totalWorkedMinutes} min worked</span>
              </div>
              {shift.timeLog.lateFlag && <Badge className="bg-amber-100 text-amber-700">Started late</Badge>}
              {shift.timeLog.leftEarlyFlag && <Badge className="bg-amber-100 text-amber-700">Left early</Badge>}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const EmptyState = ({ icon: Icon, title, description }) => (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-12 text-center">
        <Icon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">{title}</h3>
        <p className="text-slate-500">{description}</p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-12 w-full" />
        <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-40" />)}</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5" data-testid="my-shifts-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']">My Shifts</h1>
        <p className="text-slate-500 mt-1 text-base">Waiting, confirmed and past shifts.</p>
      </div>

      {/* Today's Shift banner — surfaced above the tabs when the worker
          has a live/today shift, with a single primary action. */}
      {todaysShift && (
        <Card className="border-0 shadow-md bg-gradient-to-r from-[#0f1b3d] to-[#1a2d5a] text-white" data-testid="todays-shift-banner">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-wide font-semibold text-white/70 mb-1">Today&apos;s Shift</p>
                <h3 className="text-lg font-bold" data-testid="todays-shift-branch">{todaysShift.branchName}</h3>
                <div className="flex items-center gap-3 text-sm text-white/85 mt-1 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{todaysShift.scheduledStartTime}–{todaysShift.scheduledEndTime}</span>
                  <span className="inline-flex items-center gap-1"><Banknote className="h-4 w-4" />£{Number(todaysShift.pay || 0).toFixed(2)}</span>
                </div>
              </div>
              <Button
                className="rounded-full min-h-[48px] bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold"
                onClick={() => navigate(`/worker/tracker?shiftId=${todaysShift.id}`)}
                data-testid="open-todays-shift-btn"
              >
                Open Today&apos;s Shift <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl w-full h-auto">
          <TabsTrigger value="waiting" className="flex-1 rounded-lg min-h-[44px]" data-testid="tab-waiting">
            Waiting {appliedShifts.length > 0 && `(${appliedShifts.length})`}
          </TabsTrigger>
          <TabsTrigger value="confirmed" className="flex-1 rounded-lg min-h-[44px]" data-testid="tab-confirmed">
            Confirmed {confirmed.length > 0 && `(${confirmed.length})`}
          </TabsTrigger>
          <TabsTrigger value="past" className="flex-1 rounded-lg min-h-[44px]" data-testid="tab-past">
            Past
          </TabsTrigger>
        </TabsList>

        <TabsContent value="waiting" className="space-y-3 mt-4" data-testid="waiting-tab-content">
          {appliedShifts.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-10 text-center">
                <Send className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-900 mb-1" data-testid="waiting-empty-title">You haven&apos;t accepted any shifts yet.</h3>
                <p className="text-slate-500 mb-4">Confirm your availability on a shift and it&apos;ll appear here while the manager reviews.</p>
                <Button className="rounded-full min-h-[48px] bg-[#DA291C] hover:bg-[#b91f13] text-white" onClick={() => navigate('/worker')} data-testid="waiting-empty-cta">
                  Find Shifts
                </Button>
              </CardContent>
            </Card>
          ) : appliedShifts.map(shift => <AppliedShiftCard key={shift.id} shift={shift} />)}
        </TabsContent>

        <TabsContent value="confirmed" className="space-y-4 mt-4" data-testid="confirmed-tab-content">
          {confirmed.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-10 text-center">
                <CheckCircle2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-900 mb-1" data-testid="confirmed-empty-title">No confirmed shifts yet.</h3>
                <p className="text-slate-500 mb-4">Once a manager picks you, your shift will show here.</p>
                <Button className="rounded-full min-h-[48px] bg-[#DA291C] hover:bg-[#b91f13] text-white" onClick={() => navigate('/worker')}>Find Shifts</Button>
              </CardContent>
            </Card>
          ) : confirmed.map(shift => <ShiftCard key={shift.id} shift={shift} />)}
        </TabsContent>

        <TabsContent value="past" className="space-y-4 mt-4" data-testid="past-tab-content">
          {past.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-10 text-center">
                <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">No past shifts yet.</h3>
                <p className="text-slate-500">Completed and cancelled shifts will appear here.</p>
              </CardContent>
            </Card>
          ) : past.map(shift => <ShiftCard key={shift.id} shift={shift} />)}
        </TabsContent>
      </Tabs>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!confirmCancel} onOpenChange={(open) => !open && setConfirmCancel(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Cancel this shift?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-slate-600">Let the manager at <strong>{confirmCancel?.branchName}</strong> know you can&apos;t make it. We&apos;ll notify them straight away.</p>
            {confirmCancel && isWithin24Hours(confirmCancel) && (
              <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl border border-red-200">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Heads up — late cancellation</p>
                  <p className="text-sm text-red-700">This shift starts within 24 hours, so a <strong>&pound;20 late cancellation fee</strong> will apply. Cancelling repeatedly can affect your reliability score.</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(null)} className="rounded-full min-h-[48px]">Keep the shift</Button>
            <Button
              onClick={() => handleCancelShift(confirmCancel?.id)}
              className="rounded-full bg-orange-600 hover:bg-orange-700 text-white min-h-[48px]"
              data-testid="confirm-cancel-btn"
            >
              Yes, cancel shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
